import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogoMark } from "@/components/Logo";

const DOTS = [
    { left: "12%", top: "22%", d: 0 },
    { left: "22%", top: "68%", d: 0.6 },
    { left: "78%", top: "30%", d: 1.1 },
    { left: "86%", top: "62%", d: 0.3 },
    { left: "50%", top: "16%", d: 1.5 },
    { left: "64%", top: "80%", d: 0.9 },
    { left: "32%", top: "84%", d: 1.3 },
    { left: "90%", top: "44%", d: 0.2 },
];

export default function Loader({ onDone }) {
    const [pct, setPct] = useState(0);
    const [phase, setPhase] = useState("count");

    useEffect(() => {
        document.body.classList.add("zx-lock");
        const t0 = performance.now();
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - t0) / 1650);
            setPct(Math.round(100 * (1 - Math.pow(1 - p, 3))));
            if (p < 1) raf = requestAnimationFrame(tick);
            else setPhase("sweep");
        };
        raf = requestAnimationFrame(tick);
        const t1 = setTimeout(() => setPhase("split"), 2250);
        const t2 = setTimeout(() => {
            document.body.classList.remove("zx-lock");
            onDone();
        }, 2950);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t1);
            clearTimeout(t2);
            document.body.classList.remove("zx-lock");
        };
    }, [onDone]);

    const splitting = phase === "split";

    return (
        <div className="fixed inset-0 z-[100]" data-testid="cinematic-loader" aria-label="Loading Zenithix">
            {/* split panels */}
            <motion.div
                className="absolute inset-x-0 top-0 h-1/2 bg-navy-900"
                animate={splitting ? { y: "-101%" } : { y: 0 }}
                transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            />
            <motion.div
                className="absolute inset-x-0 bottom-0 h-1/2 bg-navy-900"
                animate={splitting ? { y: "101%" } : { y: 0 }}
                transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            />
            {/* moving grid + particles */}
            <motion.div className="absolute inset-0" animate={{ opacity: splitting ? 0 : 1 }} transition={{ duration: 0.3 }}>
                <div className="mask-fade-y absolute inset-0 animate-pan-grid bg-grid opacity-50" />
                {DOTS.map((d, i) => (
                    <span
                        key={i}
                        className="absolute h-1 w-1 rounded-full bg-electric shadow-glow animate-float-y"
                        style={{ left: d.left, top: d.top, animationDelay: `${d.d}s` }}
                    />
                ))}
                <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-electric/25 blur-[90px] animate-pulse-glow" />
            </motion.div>
            {/* content */}
            <motion.div
                className="relative z-10 flex h-full flex-col items-center justify-center gap-5"
                animate={{ opacity: splitting ? 0 : 1, scale: splitting ? 1.12 : 1 }}
                transition={{ duration: 0.4, ease: "easeIn" }}
            >
                <LogoMark size={76} animated />
                <div className="font-display text-xl font-semibold tracking-[0.35em] text-ice md:text-2xl" aria-hidden>
                    {"ZENITHIX".split("").map((c, i) => (
                        <motion.span
                            key={i}
                            className="inline-block"
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.45 + i * 0.05, duration: 0.4 }}
                        >
                            {c}
                        </motion.span>
                    ))}
                </div>
                <div className="flex w-56 flex-col items-center gap-3">
                    <div className="h-px w-full overflow-hidden rounded bg-white/10">
                        <div className="h-full bg-gradient-to-r from-electric-300 to-electric shadow-glow transition-[width] duration-100" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="font-mono text-xs text-electric" data-testid="loader-counter">
                        {pct}%
                    </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-frost" aria-hidden>
                    {"WHERE INNOVATION MEETS GROWTH".split("").map((c, i) => (
                        <motion.span
                            key={i}
                            className="inline-block"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.9 + i * 0.018, duration: 0.3 }}
                        >
                            {c === " " ? "\u00A0" : c}
                        </motion.span>
                    ))}
                </div>
            </motion.div>
            {/* light sweep */}
            {phase !== "count" && (
                <motion.div
                    className="pointer-events-none absolute inset-y-0 left-0 z-20 w-1/3 bg-gradient-to-r from-transparent via-[#66B5FF99] to-transparent"
                    initial={{ x: "-140%" }}
                    animate={{ x: "340%" }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                />
            )}
        </div>
    );
}
